/**
 * Tiny useState wrapper that mirrors its value into localStorage so it
 * survives reloads on the same browser. Mirrors the
 * `ThemeProvider.readPreference` pattern (try/catch + useState
 * initializer) but factored so other components — currently the
 * apps-page filter — can keep their persistence concerns local.
 *
 * The serializer is JSON; pass `validate` to coerce unknown shapes
 * back to your initial value when storage drifts (older app shipped
 * a different shape, an extension wrote junk, etc.).
 */

import { useCallback, useState } from 'react';

export function usePersistentState<T>(
  key: string,
  initial: T,
  validate?: (value: unknown) => value is T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValueState] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return initial;
      const parsed: unknown = JSON.parse(raw);
      if (validate && !validate(parsed)) return initial;
      return parsed as T;
    } catch {
      return initial;
    }
  });

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValueState((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // Storage disabled / quota — in-memory only.
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, setValue];
}
